import classNames from 'classnames'
import React, { ChangeEventHandler, forwardRef, useCallback, useState } from 'react'
import CheckIcon from '../../assets/icons/check.svg'
import { isNullOrWhiteSpace } from '../../utilities/string'

export type CheckboxProps = {
  additionalClassName?: string
  /**
   * Note: Color palettes are only used if the variant is 'button'
   */
  color?: 'default' | 'dark' | 'primary'
  defaultChecked?: boolean
  id?: string
  label: string
  labelFormatted?: JSX.Element
  name: string
  onBlur?: ChangeEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  required?: boolean
  tooltip?: string
  validation?: true | string
  value?: string
  variant?: 'default' | 'button'
}

interface Palette {
  focusColors: string
  hoverColors: string
  selectedCheckmark: string
  selectedColors: string
  unselectedCheckmark: string
  unselectedColors: string
}

// Note: Palettes are only used if the variant is 'button'
const DarkPalette: Palette = {
  focusColors:
    'focus-within:bg-secondary-dark focus-within:ring-secondary/40 focus-within:border-secondary-alt',
  hoverColors: 'hover:border-secondary-alt hover:bg-secondary-dark',
  selectedCheckmark: 'text-white',
  selectedColors: 'bg-secondary-dark border-secondary-alt text-white',
  unselectedCheckmark: 'text-secondary-dark-alt',
  unselectedColors: 'bg-secondary-darker border-secondary-dark-alt text-white',
}

const DefaultPalette: Palette = {
  focusColors: 'focus-within:ring-primary/40 focus-within:border-primary',
  hoverColors: 'hover:border-primary',
  selectedCheckmark: 'text-primary',
  selectedColors: 'bg-neutral border-primary text-primary',
  unselectedCheckmark: 'text-neutral-lighter-alt',
  unselectedColors: 'bg-neutral border-neutral-lighter-alt text-secondary-darkest',
}

const PrimaryPalette: Palette = {
  focusColors: 'focus-within:ring-white/40 focus-within:border-white',
  hoverColors: 'hover:border-white hover:bg-white hover:text-primary',
  selectedCheckmark: 'text-primary',
  selectedColors: 'bg-white border-primary text-primary',
  unselectedCheckmark: 'text-white hover:text-primary group-hover:text-primary',
  unselectedColors: 'bg-transparent border-neutral-lighter-alt text-white',
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      additionalClassName,
      color,
      defaultChecked = false,
      id,
      label,
      labelFormatted,
      onChange: onChangeProps,
      required,
      tooltip,
      validation,
      variant = 'default',
      ...checkboxProps
    },
    ref
  ) => {
    const [checked, setChecked] = useState<boolean>(defaultChecked)

    let palette = DefaultPalette

    switch (color) {
      case 'primary':
        palette = PrimaryPalette
        break
      case 'dark':
        palette = DarkPalette
        break
      case 'default':
      default:
        palette = DefaultPalette
    }

    let labelClassName: string

    if (variant === 'button') {
      const baseClassName =
        'inline-flex justify-center items-center group transition-hover relative cursor-pointer'
      const colors = checked ? palette.selectedColors : palette.unselectedColors
      const padding = 'px-10 py-7'
      const fontSize = color === 'primary' ? 'text-t-200' : 'text-t-100'
      const fontWeight = color === 'primary' ? 'font-medium' : 'font-semibold'
      const border = 'border rounded'
      const hover = palette.hoverColors
      const focus = classNames(
        'focus-within:ring',
        palette.focusColors,
        'focus-within:outline-none'
      )

      labelClassName = classNames(
        baseClassName,
        colors,
        padding,
        fontSize,
        fontWeight,
        border,
        hover,
        focus
      )
    } else {
      labelClassName =
        'flex flex-none p-4 items-center font-t text-t-200 group transition-hover cursor-pointer'
    }

    const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      e => {
        setChecked(e.currentTarget.checked)
        onChangeProps?.(e)
      },
      [onChangeProps]
    )

    const isValid = validation === true
    const isInvalid = validation !== true && !isNullOrWhiteSpace(validation)

    return (
      <div className={classNames('relative', additionalClassName)}>
        <label className={labelClassName} title={tooltip}>
          <input
            {...checkboxProps}
            className="appearance-none opacity-0 absolute peer focus:outline-none"
            defaultChecked={defaultChecked}
            onChange={onChange}
            ref={ref}
            required={required}
            type="checkbox"
          />

          {variant === 'default' ? (
            <div
              className={classNames(
                'flex flex-none w-20 h-20 mr-10 justify-center items-center border rounded-[0.375rem] peer-focus-visible:ring transition duration-200',
                {
                  'border-secondary peer-focus-visible:ring-error/40': isInvalid,
                  [classNames({
                    'border-success peer-focus-visible:ring-success/40':
                      isValid && checked,
                    'border-primary': !isValid && checked,
                    'border-neutral-lighter-alt group-hover:border-primary peer-focus-visible:border-primary peer-focus-visible:ring-primary/40':
                      !isValid && !checked,
                  })]: !isInvalid,
                }
              )}
            >
              <CheckIcon
                className={classNames(
                  'inline-block w-[0.75rem] h-[0.75rem] fill-current transition duration-200',
                  {
                    'text-success': isValid && checked,
                    'text-primary': !isValid && !isInvalid && checked,
                    'text-error': isInvalid && checked,
                    'text-neutral-lighter-alt': !checked,
                  }
                )}
              />
            </div>
          ) : (
            <CheckIcon
              className={classNames(
                'inline-block w-15 mr-10 fill-current transition-hover',
                checked ? palette.selectedCheckmark : palette.unselectedCheckmark
              )}
            />
          )}

          <span
            className={classNames({
              'text-secondary-darkest': variant === 'default',
              'select-none': variant === 'button',
            })}
          >
            <span>{labelFormatted ?? label}</span>

            {variant === 'default' && (
              <span className="ml-5 text-shade-medium-dim">
                {required ? '(required)' : '(optional)'}
              </span>
            )}
          </span>
        </label>

        {validation !== true && !isNullOrWhiteSpace(validation) && (
          <div className="min-h-15 text-secondary font-t text-t-200 leading-[15px]">
            {validation}
          </div>
        )}
      </div>
    )
  }
)
